import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useAdvertiser } from '../contexts/AdvertiserContext';
import ChooseClientPlaceholder from './ChooseClientPlaceholder';
import { extractErrorMessage } from '../utils/errorMessage';
import { Trans, useTranslation } from 'react-i18next';

interface ConversionGoal {
  id: string;
  advertiser_id: string;
  name: string;
  type: string;
  counting_method: string;
  attribution_window_click: number;
  attribution_window_view: number;
  tracking_activity_id: string | null;
  tracking_config: { src: string; type: string; cat: string } | null;
  status: string;
  count_as_conversion?: boolean;
  is_locked?: boolean;
  created_at: string;
  updated_at: string;
}

interface GoalStats {
  total_conversions: number;
  unique_users: number;
  total_value: number;
  avg_value: number;
  attributed_users: number;
  attributed_value: number;
  attribution: {
    click_through: number;
    view_through: number;
    direct: number;
  };
}

interface GoalCode {
  goal_id: string;
  goal_name: string;
  sdk_url: string;
  page_load: string;
  custom_event: string;
  // S2S postback goals only
  method?: string;
  network?: string | null;
  postback_url?: string;
  click_param?: string;
  click_out_param?: string;
}

interface AfPostbackUrls {
  install: string;
  event: string;
}

interface AfEvent {
  af_event_name: string;
  goal_id: string;
  goal_name: string | null;
}

interface AfIntegration {
  af_app_id: string;
  af_app_id_ios: string | null;
  name: string | null;
  advertiser_id: string;
  install_goal_id: string | null;
  install_goal_name?: string | null;
  active: boolean;
  events: AfEvent[];
  postback_urls: AfPostbackUrls;
}

interface AfLookupResult {
  loading?: boolean;
  valid?: boolean;
  name?: string;
  icon?: string;
}

// `value` is the stored goal type — only the key beside it is interface text.
const GOAL_TYPES = [
  { value: 'pageload', labelKey: 'conv.typePageload', descriptionKey: 'conv.typePageloadDesc' },
  { value: 'custom', labelKey: 'conv.typeCustom', descriptionKey: 'conv.typeCustomDesc' },
  { value: 'postback', labelKey: 'conv.typePostback', descriptionKey: 'conv.typePostbackDesc' },
  { value: 'appsflyer', labelKey: 'conv.typeAppsflyer', descriptionKey: 'conv.typeAppsflyerDesc' },
  // Auto-created by the Meta lead-ads collector on the first lead — not manually creatable.
  { value: 'meta_form', labelKey: 'conv.typeMetaForm', descriptionKey: 'conv.typeMetaFormDesc', auto: true },
];

const COUNTING_METHODS = [
  { value: 'every', labelKey: 'conv.countEvery' },
  { value: 'once_per_user', labelKey: 'conv.countOncePerUser' },
];

const ConversionsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAnalyst = user?.role === 'analyst' || user?.role === 'agency_analyst';
  const { selectedAdvertiser, isAllClients } = useAdvertiser();
  const selectedAdvertiserId = selectedAdvertiser?.advertiser_id || '';
  const [goals, setGoals] = useState<ConversionGoal[]>([]);
  const [selectedGoal, setSelectedGoal] = useState<ConversionGoal | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Form state
  const [goalName, setGoalName] = useState('');
  const [goalType, setGoalType] = useState('pageload');
  const [countingMethod, setCountingMethod] = useState('every');
  const [defaultValue, setDefaultValue] = useState('');
  const [countAsConversion, setCountAsConversion] = useState(true);  // false = tracker (visits), not counted

  // Code display state
  const [goalCode, setGoalCode] = useState<GoalCode | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Stats cache
  const [statsCache, setStatsCache] = useState<Record<string, GoalStats>>({});

  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Archive eligibility
  const [canArchive, setCanArchive] = useState(false);
  const [archiveInfo, setArchiveInfo] = useState<{has_stats: boolean; is_assigned: boolean} | null>(null);

  // AppsFlyer: apps are first-class conversions in the list; events live inside an app.
  const [afIntegrations, setAfIntegrations] = useState<AfIntegration[]>([]);
  const [selectedApp, setSelectedApp] = useState<AfIntegration | null>(null);
  const [detailEventInput, setDetailEventInput] = useState('');
  const [appBusy, setAppBusy] = useState(false);
  // Per-platform store lookup (name + icon) for the selected app.
  const [appLookup, setAppLookup] = useState<{ android?: AfLookupResult; ios?: AfLookupResult }>({});
  const [showArchiveApp, setShowArchiveApp] = useState(false);
  // Create form (Trigger Type = 'appsflyer')
  const [afAppName, setAfAppName] = useState('');
  const [afAndroid, setAfAndroid] = useState('');
  const [afIos, setAfIos] = useState('');
  const [afLookup, setAfLookup] = useState<{ android?: AfLookupResult; ios?: AfLookupResult }>({});
  const [afEventInput, setAfEventInput] = useState('');
  const [afEvents, setAfEvents] = useState<string[]>([]);
  const [afPostbackUrls, setAfPostbackUrls] = useState<AfPostbackUrls | null>(null);

  useEffect(() => {
    if (selectedAdvertiserId) {
      loadGoals();
      loadAfIntegrations();
      setSelectedGoal(null);
      setSelectedApp(null);
      setIsCreating(false);
      setGoalCode(null);
    }
  }, [selectedAdvertiserId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Esc key handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showDeleteModal && !isDeleting) {
        setShowDeleteModal(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showDeleteModal, isDeleting]);

  const loadGoals = async () => {
    if (!selectedAdvertiserId) return;
    setLoading(true);
    try {
      const response = await api.get(
        `/api/conversions/goals?advertiser_id=${selectedAdvertiserId}&status=active`
      );
      const goalsData = response.data.goals || [];
      setGoals(goalsData);
      goalsData.forEach((goal: ConversionGoal) => loadGoalStats(goal.id));
    } catch (err: any) {
      console.error('Error loading goals:', err);
      setGoals([]);
    } finally {
      setLoading(false);
    }
  };

  const loadGoalStats = async (goalId: string) => {
    try {
      const response = await api.get(`/api/conversions/goals/${goalId}/stats?days=30`);
      setStatsCache(prev => ({ ...prev, [goalId]: response.data.summary }));
    } catch (err) {
      console.error('Failed to load stats:', goalId);
    }
  };

  const loadGoalCode = async (goalId: string) => {
    try {
      const response = await api.get(`/api/conversions/goals/${goalId}/code`);
      setGoalCode(response.data);
    } catch (err) {
      console.error('Failed to load code:', err);
    }
  };

  const checkCanArchive = async (goalId: string) => {
    try {
      const response = await api.get(`/api/conversions/goals/${goalId}/can-archive`);
      setCanArchive(response.data.can_archive);
      setArchiveInfo({
        has_stats: response.data.has_stats,
        is_assigned: response.data.is_assigned,
      });
    } catch (err) {
      console.error('Failed to check archive eligibility:', err);
      setCanArchive(false);
      setArchiveInfo(null);
    }
  };

  const handleSelectGoal = async (goal: ConversionGoal) => {
    setSelectedApp(null);
    setSelectedGoal(goal);
    setIsCreating(false);
    setGoalName(goal.name);
    setGoalType(goal.type);
    setCountingMethod(goal.counting_method);
    setDefaultValue((goal as any).default_value?.toString() || '');
    setCountAsConversion(goal.count_as_conversion !== false);
    setError('');
    setSuccess('');
    setCanArchive(false);
    setArchiveInfo(null);
    await Promise.all([
      loadGoalCode(goal.id),
      checkCanArchive(goal.id),
    ]);
  };

  const handleCreateNew = () => {
    setSelectedGoal(null);
    setIsCreating(true);
    setGoalName('');
    setGoalType('pageload');
    setCountingMethod('every');
    setDefaultValue('');
    setCountAsConversion(true);
    setGoalCode(null);
    setError('');
    setSuccess('');
    setCanArchive(false);
    setArchiveInfo(null);
    setAfAppName('');
    setAfAndroid('');
    setAfIos(''); setAfLookup({});
    setAfEventInput('');
    setAfEvents([]);
    setAfPostbackUrls(null);
    setSelectedApp(null);
  };

  const handleSave = async () => {
    if (!goalName.trim()) {
      setError(t('conv.enterGoalName'));
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (isCreating) {
        const response = await api.post(`/api/conversions/goals`, {
          advertiser_id: selectedAdvertiserId,
          name: goalName.trim(),
          type: goalType,
          counting_method: countingMethod,
          default_value: defaultValue ? parseFloat(defaultValue) : null,
          count_as_conversion: countAsConversion,
          // S2S postback goals have no pixel/Floodlight to create.
          create_tracking: goalType !== 'postback',
        });

        setSuccess(t('conv.goalCreated', { name: goalName }));
        await loadGoals();

        // Select the newly created goal
        const newGoalResponse = await api.get(
          `/api/conversions/goals?advertiser_id=${selectedAdvertiserId}&status=active`
        );
        const newGoal = newGoalResponse.data.goals?.find((g: ConversionGoal) => g.id === response.data.id);
        if (newGoal) {
          setSelectedGoal(newGoal);
          setIsCreating(false);
          await loadGoalCode(newGoal.id);
        }
      } else if (selectedGoal) {
        await api.patch(`/api/conversions/goals/${selectedGoal.id}`, {
          name: goalName.trim(),
          type: goalType,
          counting_method: countingMethod,
          default_value: defaultValue ? parseFloat(defaultValue) : null,
          count_as_conversion: countAsConversion,
        });
        setSuccess(t('conv.goalUpdated'));
        await loadGoals();
        setSelectedGoal({ ...selectedGoal, name: goalName.trim(), type: goalType, counting_method: countingMethod, count_as_conversion: countAsConversion });
      }
    } catch (err: any) {
      setError(extractErrorMessage(err, t('conv.saveGoalError')));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => {
    if (!selectedGoal) return;
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedGoal) return;

    setIsDeleting(true);
    try {
      await api.delete(`/api/conversions/goals/${selectedGoal.id}`);
      setShowDeleteModal(false);
      setSuccess(t('conv.goalArchived'));
      setSelectedGoal(null);
      setIsCreating(false);
      setGoalCode(null);
      await loadGoals();
    } catch (err: any) {
      setError(extractErrorMessage(err, t('conv.archiveGoalError')));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setSelectedGoal(null);
    setIsCreating(false);
    setGoalName('');
    setDefaultValue('');
    setGoalCode(null);
    setError('');
    setSuccess('');
    setCanArchive(false);
    setArchiveInfo(null);
    setAfAppName('');
    setAfAndroid('');
    setAfIos(''); setAfLookup({});
    setAfEventInput('');
    setAfEvents([]);
    setAfPostbackUrls(null);
    setSelectedApp(null);
  };

  const copyToClipboard = useCallback((text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  }, []);

  // ── AppsFlyer apps (first-class conversions in the list) ───────────────────
  const loadAfIntegrations = useCallback(async () => {
    if (!selectedAdvertiserId) return;
    try {
      const r = await api.get(`/api/appsflyer/integrations?advertiser_id=${selectedAdvertiserId}`);
      setAfIntegrations(r.data.integrations || []);
    } catch {
      setAfIntegrations([]);
    }
  }, [selectedAdvertiserId]);

  // Split the stored ids back into the two platforms (registration keeps the primary in af_app_id
  // and only fills af_app_id_ios when BOTH exist; a lone Android id contains a dot, iOS is numeric).
  const appIds = (app: AfIntegration): { android: string; ios: string } => ({
    android: app.af_app_id_ios ? app.af_app_id : (app.af_app_id.includes('.') ? app.af_app_id : ''),
    ios: app.af_app_id_ios || (!app.af_app_id.includes('.') ? app.af_app_id : ''),
  });

  // Sum each conversion goal's stats into a single app rollup for the cards.
  const appAggStats = (app: AfIntegration): GoalStats | null => {
    const ids = [app.install_goal_id, ...app.events.map(e => e.goal_id)].filter(Boolean) as string[];
    const rows = ids.map(id => statsCache[id]).filter(Boolean) as GoalStats[];
    if (!rows.length) return null;
    return rows.reduce((a, s) => ({
      total_conversions: a.total_conversions + (s.total_conversions || 0),
      unique_users: a.unique_users + (s.unique_users || 0),
      total_value: a.total_value + (s.total_value || 0),
      avg_value: 0,
      attributed_users: a.attributed_users + (s.attributed_users || 0),
      attributed_value: a.attributed_value + (s.attributed_value || 0),
      attribution: {
        click_through: a.attribution.click_through + (s.attribution?.click_through || 0),
        view_through: a.attribution.view_through + (s.attribution?.view_through || 0),
        direct: a.attribution.direct + (s.attribution?.direct || 0),
      },
    }), { total_conversions: 0, unique_users: 0, total_value: 0, avg_value: 0,
          attributed_users: 0, attributed_value: 0,
          attribution: { click_through: 0, view_through: 0, direct: 0 } } as GoalStats);
  };

  // Resolve one platform's store name + icon into appLookup (doesn't touch the create form).
  const lookupAppInto = async (platform: 'ios' | 'android', appId: string) => {
    setAppLookup(prev => ({ ...prev, [platform]: { loading: true } }));
    try {
      const r = await api.get(`/api/appsflyer/app-lookup?platform=${platform}&app_id=${encodeURIComponent(appId)}`);
      const d = r.data || {};
      setAppLookup(prev => ({ ...prev, [platform]: { valid: !!d.valid, name: d.name, icon: d.icon } }));
    } catch {
      setAppLookup(prev => ({ ...prev, [platform]: { valid: false } }));
    }
  };

  const handleSelectApp = (app: AfIntegration) => {
    setSelectedApp(app);
    setSelectedGoal(null);
    setIsCreating(false);
    setGoalCode(null);
    setDetailEventInput('');
    setError('');
    setSuccess('');
    // Load each conversion goal's stats for the rollup + per-row figures.
    [app.install_goal_id, ...app.events.map(e => e.goal_id)]
      .filter(Boolean).forEach(id => loadGoalStats(id as string));
    // Resolve the store name + icon for each platform id.
    setAppLookup({});
    const { android, ios } = appIds(app);
    if (android) lookupAppInto('android', android);
    if (ios) lookupAppInto('ios', ios);
  };

  const handleRemoveEvent = async (app: AfIntegration, eventName: string) => {
    setAppBusy(true); setError(''); setSuccess('');
    try {
      await api.delete(`/api/appsflyer/integrations/${encodeURIComponent(app.af_app_id)}/events/${encodeURIComponent(eventName)}`);
      const r = await api.get(`/api/appsflyer/integrations?advertiser_id=${selectedAdvertiserId}`);
      const list: AfIntegration[] = r.data.integrations || [];
      setAfIntegrations(list);
      setSelectedApp(list.find(a => a.af_app_id === app.af_app_id) || null);
      setSuccess(t('conv.eventRemoved', { name: eventName }));
      await loadGoals();
    } catch (err: any) {
      setError(extractErrorMessage(err, t('conv.removeEventError')));
    } finally {
      setAppBusy(false);
    }
  };

  const handleArchiveApp = async () => {
    if (!selectedApp) return;
    setAppBusy(true); setError(''); setSuccess('');
    try {
      await api.delete(`/api/appsflyer/integrations/${encodeURIComponent(selectedApp.af_app_id)}`);
      setShowArchiveApp(false);
      setSelectedApp(null);
      setSuccess(t('conv.appArchived'));
      await loadAfIntegrations();
      await loadGoals();
    } catch (err: any) {
      setError(extractErrorMessage(err, t('conv.archiveAppError')));
    } finally {
      setAppBusy(false);
    }
  };

  const handleAddEventToApp = async (app: AfIntegration, ev: string) => {
    const name = ev.trim();
    if (!name) return;
    setAppBusy(true); setError(''); setSuccess('');
    try {
      await api.post(`/api/appsflyer/integrations/${encodeURIComponent(app.af_app_id)}/events`, {
        af_event_name: name,
      });
      setDetailEventInput('');
      const r = await api.get(`/api/appsflyer/integrations?advertiser_id=${selectedAdvertiserId}`);
      const list: AfIntegration[] = r.data.integrations || [];
      setAfIntegrations(list);
      setSelectedApp(list.find(a => a.af_app_id === app.af_app_id) || null);
      setSuccess(t('conv.eventAdded', { name }));
      await loadGoals();
    } catch (err: any) {
      setError(extractErrorMessage(err, t('conv.addEventError')));
    } finally {
      setAppBusy(false);
    }
  };

  // ── AppsFlyer create form (Trigger Type = 'appsflyer') ─────────────────────
  const afValid = !!afAppName.trim() && (!!afAndroid.trim() || !!afIos.trim());

  // Validate an app id against the store (iOS = iTunes, Android = Play) and auto-fill the App
  // name when it's still empty. Best-effort — a miss just shows "not found".
  const lookupApp = async (platform: 'ios' | 'android', appId: string) => {
    const id = appId.trim();
    if (!id) { setAfLookup(prev => ({ ...prev, [platform]: undefined })); return; }
    setAfLookup(prev => ({ ...prev, [platform]: { loading: true } }));
    try {
      const r = await api.get(`/api/appsflyer/app-lookup?platform=${platform}&app_id=${encodeURIComponent(id)}`);
      const d = r.data || {};
      setAfLookup(prev => ({ ...prev, [platform]: { valid: !!d.valid, name: d.name, icon: d.icon } }));
      if (d.valid && d.name && !afAppName.trim()) setAfAppName(d.name);
    } catch {
      setAfLookup(prev => ({ ...prev, [platform]: { valid: false } }));
    }
  };

  const renderAfLookup = (r?: AfLookupResult) => {
    if (!r) return null;
    return (
      <div style={{ marginTop: 6, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
        {r.loading ? (
          <span style={{ color: '#8a8a8a' }}>{t('conv.lookingUp')}</span>
        ) : r.valid ? (
          <>
            {r.icon && <img src={r.icon} alt="" width={22} height={22} style={{ borderRadius: 5 }} />}
            <span style={{ color: '#1e9e57', fontWeight: 600 }}>✓ {r.name}</span>
          </>
        ) : (
          <span style={{ color: '#c0392b' }}>{t('conv.appNotFound')}</span>
        )}
      </div>
    );
  };

  const addAfEventChip = () => {
    const ev = afEventInput.trim();
    if (!ev) return;
    setAfEvents(prev => (prev.includes(ev) ? prev : [...prev, ev]));
    setAfEventInput('');
  };

  const removeAfEventChip = (ev: string) => setAfEvents(prev => prev.filter(x => x !== ev));

  const handleCreateAppsflyer = async () => {
    if (!afValid) {
      setError(t('conv.appNameAndId'));
      return;
    }
    setSaving(true); setError(''); setSuccess(''); setAfPostbackUrls(null);
    try {
      // Create the app (auto-creates its install goal); returns primary af_app_id + urls.
      const res = await api.post('/api/appsflyer/integrations', {
        advertiser_id: selectedAdvertiserId,
        name: afAppName.trim(),
        af_app_id_android: afAndroid.trim() || undefined,
        af_app_id_ios: afIos.trim() || undefined,
      });
      const afAppId: string = res.data.af_app_id;
      // Pre-create each typed in-app event's goal (skip blanks / literal "install").
      for (const raw of afEvents) {
        const ev = raw.trim();
        if (!ev || ev.toLowerCase() === 'install') continue;
        await api.post(`/api/appsflyer/integrations/${encodeURIComponent(afAppId)}/events`, {
          af_event_name: ev,
        });
      }
      setAfPostbackUrls(res.data.postback_urls || null);
      setSuccess(t('conv.appSaved', { name: res.data.name || afAppId }));
      await loadGoals();
      // Refresh the app list and OPEN the new app (it's now a first-class conversion).
      const r = await api.get(`/api/appsflyer/integrations?advertiser_id=${selectedAdvertiserId}`);
      const list: AfIntegration[] = r.data.integrations || [];
      setAfIntegrations(list);
      setAfAppName(''); setAfAndroid(''); setAfIos(''); setAfEventInput(''); setAfEvents([]);
      const created = list.find(a => a.af_app_id === afAppId) || null;
      if (created) {
        setIsCreating(false);
        setSelectedApp(created);
      }
    } catch (err: any) {
      setError(extractErrorMessage(err, t('conv.saveAppError')));
    } finally {
      setSaving(false);
    }
  };

  // AppsFlyer goals live under their app, not as standalone web-goal rows.
  const afGoalIds = new Set<string>();
  afIntegrations.forEach(app => {
    if (app.install_goal_id) afGoalIds.add(app.install_goal_id);
    app.events.forEach(e => { if (e.goal_id) afGoalIds.add(e.goal_id); });
  });
  const filteredGoals = goals.filter(goal =>
    !afGoalIds.has(goal.id) &&
    goal.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredApps = afIntegrations.filter(app =>
    (app.name || app.af_app_id).toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getTypeLabel = (type: string) => {
    const found = GOAL_TYPES.find(gt => gt.value === type);
    return found ? t(found.labelKey) : type;
  };

  const formatNumber = (num: number) => {
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  if (isAllClients) return <ChooseClientPlaceholder pageKey="nav.conversions" />;

  return (
    <>
      {/* Header */}
      <div className="header">
        <div className="header-title">
          <h1>{t('conv.title')}</h1>
          <p className="header-subtitle">{t('conv.subtitle')}</p>
        </div>
      </div>

      {/* Main Layout */}
      <div className="url-lists-layout">
        {/* Left Panel - Goals List */}
        <aside className="url-lists-panel">
          {/* Search */}
          <div className="panel-section">
            <div className="search-wrapper">
              <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                className="search-input"
                placeholder={t('conv.searchGoals')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Add Button */}
          {!isAnalyst && (
          <button className="add-list-btn" onClick={handleCreateNew}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            <span>{t('conv.addGoal')}</span>
          </button>
          )}

          {/* Goals List */}
          <div className="lists-container">
            {loading ? (
              <div className="lists-loading">
                <div className="spinner"></div>
                <span>{t('conv.loadingGoals')}</span>
              </div>
            ) : (filteredGoals.length === 0 && filteredApps.length === 0) ? (
              <div className="lists-empty">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{searchQuery ? t('conv.noMatching') : t('conv.noConversions')}</span>
              </div>
            ) : (
              <>
              {filteredGoals.map(goal => {
                const stats = statsCache[goal.id];
                return (
                  <div
                    key={goal.id}
                    className={`list-item ${selectedGoal?.id === goal.id ? 'active' : ''}`}
                    onClick={() => handleSelectGoal(goal)}
                  >
                    <div className="list-item-icon">
                      {goal.type === 'pageload' ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="16 18 22 12 16 6"></polyline>
                          <polyline points="8 6 2 12 8 18"></polyline>
                        </svg>
                      )}
                    </div>
                    <div className="list-item-content">
                      <div className="list-item-name" style={goal.is_locked ? { color: '#888' } : undefined}>
                        {goal.is_locked && (
                          <span title={t('conv.legacyReadOnly')} style={{ marginRight: 6, color: '#888' }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ verticalAlign: 'middle' }}>
                              <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm9 14H6V10h12v10zm-6-3c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"/>
                            </svg>
                          </span>
                        )}
                        {goal.name}
                      </div>
                      <div className="list-item-meta">
                        {getTypeLabel(goal.type)} · {t('conv.convCount', {
                          count: stats ? stats.total_conversions : 0,
                          value: stats ? formatNumber(stats.total_conversions) : '0',
                        })}
                      </div>
                    </div>
                    <svg className="list-item-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </div>
                );
              })}

              {filteredApps.length > 0 && (
                <>
                  <div style={{ padding: '10px 12px 4px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: '#8a8a8a' }}>{t('conv.appsflyerApps')}</div>
                  {filteredApps.map(app => (
                    <div
                      key={`af-${app.af_app_id}`}
                      className={`list-item ${selectedApp?.af_app_id === app.af_app_id ? 'active' : ''}`}
                      onClick={() => handleSelectApp(app)}
                    >
                      <div className="list-item-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="7" y="2" width="10" height="20" rx="2"></rect>
                          <line x1="11" y1="18" x2="13" y2="18"></line>
                        </svg>
                      </div>
                      <div className="list-item-content">
                        <div className="list-item-name">
                          {app.name || app.af_app_id}
                          <span style={{ marginLeft: 6, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.03em', color: '#9a6cff' }}>{t('conv.appBadge')}</span>
                        </div>
                        <div className="list-item-meta">
                          AppsFlyer · {t('conv.conversionsCount', { count: app.events.length + 1 })}
                        </div>
                      </div>
                      <svg className="list-item-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </div>
                  ))}
                </>
              )}
              </>
            )}
          </div>
        </aside>

        {/* Right Panel - Editor */}
        <div className="url-editor-panel">
          {selectedApp ? (
            <div className="editor-content">
              <div className="editor-header">
                <h2>{selectedApp.name || selectedApp.af_app_id}</h2>
                <p style={{ margin: '6px 0 0', fontSize: 13, lineHeight: 1.5, color: '#8a8a8a', maxWidth: 640 }}>
                  <Trans i18nKey="conv.afHeaderDesc" components={{ b: <strong /> }} />
                </p>
              </div>

              {error && (
                <div className="alert alert-error">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                  <span>{error}</span><button onClick={() => setError('')}>&times;</button>
                </div>
              )}
              {success && (
                <div className="alert alert-success">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeLinecap="round" strokeLinejoin="round"></path><polyline points="22 4 12 14.01 9 11.01" strokeLinecap="round" strokeLinejoin="round"></polyline></svg>
                  <span>{success}</span><button onClick={() => setSuccess('')}>&times;</button>
                </div>
              )}

              {/* Rollup stats across the install goal + all event goals (last 30 days) */}
              {(() => {
                const s = appAggStats(selectedApp);
                if (!s) return null;
                return (
                  <div className="conversion-stats-grid">
                    <div className="conversion-stat-card">
                      <div className="stat-value">{s.attribution.click_through}</div>
                      <div className="stat-label">{t('conv.adClicks')}</div>
                    </div>
                    <div className="conversion-stat-card">
                      <div className="stat-value">{s.attribution.view_through}</div>
                      <div className="stat-label">{t('conv.adViews')}</div>
                    </div>
                    <div className="conversion-stat-card">
                      <div className="stat-value">{s.attributed_users}</div>
                      <div className="stat-label">{t('conv.uniqueUsers')}</div>
                    </div>
                    <div className="conversion-stat-card">
                      <div className="stat-value">
                        {s.attributed_value > 0
                          ? <><span className="currency-symbol">€</span>{s.attributed_value.toFixed(0)}</>
                          : '—'}
                      </div>
                      <div className="stat-label">{t('conv.totalValue')}</div>
                    </div>
                    <div className="conversion-stat-card conversion-stat-card-secondary">
                      <div className="stat-value">{s.total_conversions}</div>
                      <div className="stat-label">{t('conv.total')}</div>
                      <div className="stat-sublabel">{t('conv.inclOtherSources')}</div>
                    </div>
                  </div>
                );
              })()}

              <div className="editor-form">
                {/* Platform apps — icon + store name + id, one card per platform */}
                <div className="form-group">
                  <label className="form-label">{t('conv.app')}</label>
                  <p className="step-hint" style={{ marginTop: 0 }}>
                    {t('conv.platformsHint')}
                  </p>
                  <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
                    {(['android', 'ios'] as const).map(platform => {
                      const id = appIds(selectedApp)[platform];
                      if (!id) return null;
                      const look = appLookup[platform];
                      return (
                        <div key={platform} style={{ display: 'flex', alignItems: 'center', gap: 12,
                          padding: '10px 12px', border: '1px solid #ececec', borderRadius: 10, background: '#fafafa' }}>
                          {look?.icon
                            ? <img src={look.icon} alt="" width={40} height={40} style={{ borderRadius: 9, flexShrink: 0 }} />
                            : <div style={{ width: 40, height: 40, borderRadius: 9, background: '#eee', flexShrink: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: 18 }}>
                                {platform === 'ios' ? '' : '▸'}
                              </div>}
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a1a', whiteSpace: 'nowrap',
                              overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {look?.loading ? t('conv.lookingUp') : (look?.name || (look?.valid === false ? t('conv.notFoundInStore') : '—'))}
                            </div>
                            <div style={{ fontSize: 12, color: '#8a8a8a', whiteSpace: 'nowrap',
                              overflow: 'hidden', textOverflow: 'ellipsis' }}>{id}</div>
                          </div>
                          <span className="conversion-stat-card-secondary" style={{ padding: '3px 9px', borderRadius: 6, fontSize: 11, flexShrink: 0 }}>
                            {platform === 'ios' ? 'iOS' : 'Android'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Conversions — install (auto) + each event, with per-goal figures */}
                <div className="form-group">
                  <label className="form-label">{t('conv.title')}</label>
                  <div style={{ display: 'grid', gap: 6, marginTop: 6 }}>
                    {[
                      { key: '__install__', name: selectedApp.install_goal_name || t('conv.appInstall'),
                        goalId: selectedApp.install_goal_id, auto: true, eventName: null as string | null },
                      ...selectedApp.events.map(ev => ({
                        key: ev.af_event_name, name: ev.goal_name || ev.af_event_name,
                        goalId: ev.goal_id, auto: false, eventName: ev.af_event_name,
                      })),
                    ].map(row => {
                      const st = row.goalId ? statsCache[row.goalId] : undefined;
                      const count = st ? st.total_conversions : 0;
                      return (
                        <div key={row.key} style={{ display: 'flex', alignItems: 'center', gap: 10,
                          padding: '9px 12px', border: '1px solid #ececec', borderRadius: 9, background: '#fff' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 13.5, color: '#1a1a1a', whiteSpace: 'nowrap',
                              overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.name}</div>
                            {row.auto && <div style={{ fontSize: 11.5, color: '#8a8a8a', marginTop: 1 }}>{t('conv.createdAutomatically')}</div>}
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: 14 }}>{count}</div>
                            <div style={{ fontSize: 10.5, color: '#9a9a9a', textTransform: 'uppercase', letterSpacing: '.03em' }}>{t('conv.conv30d')}</div>
                          </div>
                          {row.auto
                            ? <span className="conversion-stat-card-secondary" style={{ padding: '3px 9px', borderRadius: 6, fontSize: 11, flexShrink: 0 }}>{t('conv.autoBadge')}</span>
                            : (!isAnalyst && (
                                <button className="icon-btn-danger" title={t('conv.removeEvent')} disabled={appBusy}
                                  onClick={() => row.eventName && handleRemoveEvent(selectedApp, row.eventName)}
                                  style={{ flexShrink: 0, border: 'none', background: 'transparent', cursor: 'pointer', color: '#c0392b', padding: 4 }}>
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="3 6 5 6 21 6"></polyline>
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                  </svg>
                                </button>
                              ))}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {!isAnalyst && (
                  <div className="form-group">
                    <label className="form-label">{t('conv.addInAppEvent')}</label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input type="text" className="form-input" placeholder="af_purchase" value={detailEventInput}
                        onChange={(e) => setDetailEventInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddEventToApp(selectedApp, detailEventInput); } }}
                        style={{ maxWidth: 280 }} />
                      <button className="btn-secondary" onClick={() => handleAddEventToApp(selectedApp, detailEventInput)} disabled={appBusy || !detailEventInput.trim()}>{t('conv.add')}</button>
                    </div>
                    <p className="step-hint">{t('conv.addEventsHint')}</p>
                  </div>
                )}
              </div>

              {!isAnalyst && (
                <div className="editor-actions">
                  <div className="editor-actions-left">
                    <button className="btn-danger" onClick={() => setShowArchiveApp(true)} disabled={appBusy}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                      {t('conv.archiveApp')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : !selectedGoal && !isCreating ? (
            <div className="editor-empty">
              <div className="editor-empty-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                </svg>
              </div>
              <h3>{t('conv.selectGoal')}</h3>
              <p>{t('conv.chooseGoalHint')}</p>
              {!isAnalyst && (
              <button className="editor-empty-btn" onClick={handleCreateNew}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                {t('conv.createNewGoal')}
              </button>
              )}
            </div>
          ) : (
            <div className="editor-content">
              {/* Editor Header */}
              <div className="editor-header">
                <h2>{isCreating ? t('conv.newGoal') : t('conv.editGoal')}</h2>
              </div>

              {/* Alerts */}
              {error && (
                <div className="alert alert-error">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <span>{error}</span>
                  <button onClick={() => setError('')}>&times;</button>
                </div>
              )}
              {success && (
                <div className="alert alert-success">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeLinecap="round" strokeLinejoin="round"></path>
                    <polyline points="22 4 12 14.01 9 11.01" strokeLinecap="round" strokeLinejoin="round"></polyline>
                  </svg>
                  <span>{success}</span>
                  <button onClick={() => setSuccess('')}>&times;</button>
                </div>
              )}

              {/* Stats Cards */}
              {!isCreating && selectedGoal && statsCache[selectedGoal.id] && (
                <div className="conversion-stats-grid">
                  <div className="conversion-stat-card">
                    <div className="stat-value">{statsCache[selectedGoal.id].attribution?.click_through || 0}</div>
                    <div className="stat-label">{t('conv.adClicks')}</div>
                  </div>
                  <div className="conversion-stat-card">
                    <div className="stat-value">{statsCache[selectedGoal.id].attribution?.view_through || 0}</div>
                    <div className="stat-label">{t('conv.adViews')}</div>
                  </div>
                  <div className="conversion-stat-card">
                    <div className="stat-value">{statsCache[selectedGoal.id].attributed_users || 0}</div>
                    <div className="stat-label">{t('conv.uniqueUsers')}</div>
                  </div>
                  <div className="conversion-stat-card">
                    <div className="stat-value">
                      {(statsCache[selectedGoal.id].attributed_value || 0) > 0
                        ? <><span className="currency-symbol">€</span>{statsCache[selectedGoal.id].attributed_value.toFixed(0)}</>
                        : '—'}
                    </div>
                    <div className="stat-label">{t('conv.totalValue')}</div>
                  </div>
                  <div className="conversion-stat-card conversion-stat-card-secondary">
                    <div className="stat-value">{statsCache[selectedGoal.id].total_conversions || 0}</div>
                    <div className="stat-label">{t('conv.total')}</div>
                    <div className="stat-sublabel">{t('conv.inclOtherSources')}</div>
                  </div>
                </div>
              )}

              {/* Form */}
              <div className="editor-form">
                <div className="form-row">
                  {goalType === 'appsflyer' ? (
                    // App name sits in the left half (where Goal Name is) so the row stays stable
                    // and Trigger Type keeps its right position.
                    <div className="form-group form-group-half">
                      <label className="form-label">{t('conv.appName')}</label>
                      <input type="text" className="form-input" placeholder={t('conv.appNamePh')} value={afAppName} onChange={(e) => setAfAppName(e.target.value)} />
                    </div>
                  ) : (
                    <div className="form-group form-group-half">
                      <label className="form-label">{t('conv.goalName')}</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder={t('conv.goalNamePh')}
                        value={goalName}
                        onChange={(e) => setGoalName(e.target.value)}
                      />
                    </div>
                  )}
                  <div className="form-group form-group-half">
                    <label className="form-label">{t('conv.triggerType')}</label>
                    <select
                      className="form-select"
                      value={goalType}
                      onChange={(e) => setGoalType(e.target.value)}
                      disabled={!isCreating && !!selectedGoal}
                    >
                      {GOAL_TYPES.filter(type => !(type as any).auto).map(type => (
                        <option key={type.value} value={type.value}>
                          {t(type.labelKey)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* AppsFlyer app — inline fields below the top row (App name lives in the top row) */}
                {goalType === 'appsflyer' && (
                  <>
                    <div className="form-row">
                      <div className="form-group form-group-half">
                        <label className="form-label">{t('conv.androidAppId')}</label>
                        <input type="text" className="form-input" placeholder="com.package.app" value={afAndroid}
                          onChange={(e) => setAfAndroid(e.target.value)}
                          onBlur={(e) => lookupApp('android', e.target.value)} />
                        {renderAfLookup(afLookup.android)}
                      </div>
                      <div className="form-group form-group-half">
                        <label className="form-label">{t('conv.iosAppId')}</label>
                        <input type="text" className="form-input" placeholder="id123456789" value={afIos}
                          onChange={(e) => setAfIos(e.target.value)}
                          onBlur={(e) => lookupApp('ios', e.target.value)} />
                        {renderAfLookup(afLookup.ios)}
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">{t('conv.inAppEventsOptional')}</label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input type="text" className="form-input" placeholder="af_purchase" value={afEventInput}
                          onChange={(e) => setAfEventInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAfEventChip(); } }}
                          style={{ maxWidth: 280 }} />
                        <button className="btn-secondary" onClick={addAfEventChip} disabled={!afEventInput.trim()}>{t('conv.add')}</button>
                      </div>
                      {afEvents.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                          {afEvents.map(ev => (
                            <span key={ev} className="conversion-stat-card-secondary" style={{ padding: '4px 8px 4px 10px', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              {ev}
                              <button type="button" onClick={() => removeAfEventChip(ev)} title={t('conv.remove')}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 14, lineHeight: 1, padding: 0 }}>&times;</button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div style={{ marginTop: 4, marginBottom: 10, padding: '10px 14px',
                        background: 'rgba(255,107,74,0.09)', border: '1px solid rgba(255,107,74,0.28)',
                        borderRadius: 8 }}>
                      <div style={{ fontWeight: 700, color: '#E9552F', fontSize: 14 }}>
                        <Trans i18nKey="conv.installAutoTitle" components={{ b: <strong /> }} />
                      </div>
                      <div className="step-hint" style={{ marginTop: 4 }}>
                        <Trans i18nKey="conv.afEventsHint" components={{ c: <code>af_purchase</code> }} />
                      </div>
                    </div>

                  </>
                )}

                {goalType !== 'appsflyer' && (
                <div className="form-row">
                  <div className="form-group form-group-half">
                    <label className="form-label">{t('conv.countingMethod')}</label>
                    <select
                      className="form-select"
                      value={countingMethod}
                      onChange={(e) => setCountingMethod(e.target.value)}
                    >
                      {COUNTING_METHODS.map(method => (
                        <option key={method.value} value={method.value}>
                          {t(method.labelKey)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group form-group-half">
                    <label className="form-label">{t('conv.defaultValue')}</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder={t('conv.optional')}
                      value={defaultValue}
                      onChange={(e) => setDefaultValue(e.target.value)}
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{t('conv.countsAs')}</label>
                    <select
                      className="form-select"
                      value={countAsConversion ? 'conversion' : 'tracker'}
                      onChange={(e) => setCountAsConversion(e.target.value === 'conversion')}
                    >
                      <option value="conversion">{t('conv.countsConversion')}</option>
                      <option value="tracker">{t('conv.countsTracker')}</option>
                    </select>
                  </div>
                </div>

                {/* Server-to-server postback setup (CPA network) */}
                {goalCode && goalCode.method === 'postback' && (
                  <div className="installation-guide">
                    <div className="guide-header">
                      <div className="guide-header-content">
                        <h3>{t('conv.postbackSetup')}</h3>
                        <p>{t('conv.postbackSubtitle')}</p>
                      </div>
                    </div>
                    <div className="guide-steps">
                      <div className="guide-step">
                        <div className="step-indicator">
                          <div className="step-number">1</div>
                          <div className="step-line"></div>
                        </div>
                        <div className="step-content">
                          <div className="step-title">{t('conv.postbackStep1')}</div>
                          <div className="step-desc">
                            <Trans i18nKey="conv.postbackStep1Desc" components={{
                              c1: <code>{'{CLICK_ID}'}</code>,
                              c2: <code>{'{TXID}'}</code>,
                            }} />
                          </div>
                          <div className="code-snippet-inline">
                            <code>{goalCode.postback_url}</code>
                            <button
                              className={`btn-copy-sm ${copiedField === 'pb_url' ? 'copied' : ''}`}
                              onClick={() => copyToClipboard(goalCode.postback_url || '', 'pb_url')}
                            >
                              {copiedField === 'pb_url' ? t('conv.copied') : t('conv.copy')}
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="guide-step">
                        <div className="step-indicator">
                          <div className="step-number">2</div>
                        </div>
                        <div className="step-content">
                          <div className="step-title">{t('conv.postbackStep2')}</div>
                          <div className="step-desc">
                            <Trans i18nKey="conv.postbackStep2Desc" components={{
                              c1: <code>{goalCode.click_out_param}</code>,
                              c2: <code>{'{CLICK_ID}'}</code>,
                            }} />
                          </div>
                          <p className="step-hint">
                            {t('conv.postbackStep2Hint')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Installation Guide Section */}
                {goalCode && goalCode.method !== 'postback' && (
                  <div className="installation-guide">
                    <div className="guide-header">
                      <div className="guide-header-content">
                        <h3>{t('conv.installation')}</h3>
                        <p>{t(goalType === 'pageload' ? 'conv.installPageload' : 'conv.installCustom')}</p>
                      </div>
                    </div>

                    {goalType === 'pageload' ? (
                      <div className="guide-steps">
                        {/* Step 1 — the loader, everywhere. Without it the ad tags are
                            gone by the time the visitor reaches the thank-you page. */}
                        <div className="guide-step">
                          <div className="step-indicator">
                            <div className="step-number">1</div>
                            <div className="step-line"></div>
                          </div>
                          <div className="step-content">
                            <div className="step-title">{t('conv.sdkEveryPage')}</div>
                            <div className="step-desc">
                              <Trans i18nKey="conv.sdkEveryPageDesc" components={{
                                c: <code>&lt;head&gt;</code>,
                                b: <strong />,
                              }} />
                            </div>
                            <div className="code-snippet-inline">
                              <code>{`<script src="${goalCode.sdk_url}"></script>`}</code>
                              <button
                                className={`btn-copy-sm ${copiedField === 'sdk_pageload' ? 'copied' : ''}`}
                                onClick={() => copyToClipboard(`<script src="${goalCode.sdk_url}"></script>`, 'sdk_pageload')}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  {copiedField === 'sdk_pageload' ? (
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                  ) : (
                                    <>
                                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                    </>
                                  )}
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Step 2 — the conversion itself, confirmation page only. */}
                        <div className="guide-step">
                          <div className="step-indicator">
                            <div className="step-number">2</div>
                          </div>
                          <div className="step-content">
                            <div className="step-title">{t('conv.recordOnConfirmation')}</div>
                            <div className="step-desc">
                              <Trans i18nKey="conv.recordOnConfirmationDesc" components={{ c: <code>&lt;/body&gt;</code> }} />
                            </div>
                            <div className="code-panel">
                              <div className="code-panel-body">
                                <pre><code>{`<script>
  SenseTracker.pageConversion('${goalCode.goal_id}', {
    value: ${defaultValue || '0'},        // Replace with order value, e.g. 99.99
    order_id: ''     // Replace with order ID, e.g. 'ORD-12345'
  });
</script>`}</code></pre>
                                <button
                                  className={`btn-copy-premium ${copiedField === 'pageload' ? 'copied' : ''}`}
                                  onClick={() => copyToClipboard(`<script>\n  SenseTracker.pageConversion('${goalCode.goal_id}', {\n    value: ${defaultValue || '0'},        // Replace with order value, e.g. 99.99\n    order_id: ''     // Replace with order ID, e.g. 'ORD-12345'\n  });\n</script>`, 'pageload')}
                                >
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    {copiedField === 'pageload' ? (
                                      <polyline points="20 6 9 17 4 12"></polyline>
                                    ) : (
                                      <>
                                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                      </>
                                    )}
                                  </svg>
                                  {copiedField === 'pageload' ? t('conv.copied') : t('conv.copy')}
                                </button>
                              </div>
                            </div>
                            <p className="step-hint"><Trans i18nKey="conv.valueOrderIdOptional" components={{
                              c1: <code>value</code>, c2: <code>order_id</code> }} /></p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="guide-steps">
                        {/* Step 1 */}
                        <div className="guide-step">
                          <div className="step-indicator">
                            <div className="step-number">1</div>
                            <div className="step-line"></div>
                          </div>
                          <div className="step-content">
                            <div className="step-title">{t('conv.sdkOnce')}</div>
                            <div className="step-desc">
                              <Trans i18nKey="conv.sdkOnceDesc" components={{
                                c1: <code>&lt;head&gt;</code>,
                                c2: <code>&lt;/body&gt;</code>,
                              }} />
                            </div>
                            <div className="code-snippet-inline">
                              <code>{`<script src="${goalCode.sdk_url}"></script>`}</code>
                              <button
                                className={`btn-copy-sm ${copiedField === 'sdk' ? 'copied' : ''}`}
                                onClick={() => copyToClipboard(`<script src="${goalCode.sdk_url}"></script>`, 'sdk')}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  {copiedField === 'sdk' ? (
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                  ) : (
                                    <>
                                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                    </>
                                  )}
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Step 2 */}
                        <div className="guide-step">
                          <div className="step-indicator">
                            <div className="step-number">2</div>
                          </div>
                          <div className="step-content">
                            <div className="step-title">{t('conv.triggerOnAction')}</div>
                            <div className="step-desc">
                              {t('conv.triggerOnActionDesc')}
                            </div>
                            <div className="code-snippet-inline">
                              <code>{`SenseTracker.convert('${goalCode.goal_id}', {value: ${defaultValue || '0'}, order_id: ''});`}</code>
                              <button
                                className={`btn-copy-sm ${copiedField === 'custom' ? 'copied' : ''}`}
                                onClick={() => copyToClipboard(`SenseTracker.convert('${goalCode.goal_id}', {value: ${defaultValue || '0'}, order_id: ''});`, 'custom')}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  {copiedField === 'custom' ? (
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                  ) : (
                                    <>
                                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                    </>
                                  )}
                                </svg>
                              </button>
                            </div>
                            <p className="step-hint"><Trans i18nKey="conv.valueOrderIdOptional" components={{
                              c1: <code>value</code>, c2: <code>order_id</code> }} /></p>
                          </div>
                        </div>

                        {/* Examples */}
                        <div className="examples-section">
                          <div className="examples-title">{t('conv.examples')}</div>

                          <div className="example-item">
                            <div className="example-label">{t('conv.exButtonClick')}</div>
                            <pre><code>{`<button onclick="SenseTracker.convert('${goalCode.goal_id}', {value: ${defaultValue || '0'}})">Buy Now</button>`}</code></pre>
                          </div>

                          <div className="example-item">
                            <div className="example-label">{t('conv.exFormSubmit')}</div>
                            <pre><code>{`<form onsubmit="SenseTracker.convert('${goalCode.goal_id}')">`}</code></pre>
                          </div>

                          <div className="example-item">
                            <div className="example-label">{t('conv.exPhoneCall')}</div>
                            <pre><code>{`<a href="tel:+1234567890" onclick="SenseTracker.convert('${goalCode.goal_id}')">Call Us</a>`}</code></pre>
                          </div>

                          <div className="example-item">
                            <div className="example-label">JavaScript:</div>
                            <pre><code>{`document.getElementById('my-button').addEventListener('click', function() {
  SenseTracker.convert('${goalCode.goal_id}', { value: 99.99, order_id: 'ORD-123' });
});`}</code></pre>
                          </div>

                          <p className="examples-note"><Trans i18nKey="conv.examplesNote"
                            components={{ c: <code>SenseTracker.convert()</code> }} /></p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

              </div>

              {/* Actions */}
              <div className="editor-actions">
                <div className="editor-actions-left">
                  {!isAnalyst && !isCreating && selectedGoal && canArchive && !selectedGoal.is_locked && (
                    <button className="btn-danger" onClick={handleDeleteClick} disabled={saving}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                      {t('conv.archive')}
                    </button>
                  )}
                </div>
                {!isAnalyst && !selectedGoal?.is_locked && (
                <div className="editor-actions-right">
                  <button className="btn-secondary" onClick={handleCancel} disabled={saving}>
                    {t('conv.cancel')}
                  </button>
                  {goalType !== 'appsflyer' && (
                  <button
                    className="btn-primary"
                    onClick={handleSave}
                    disabled={saving || !goalName.trim()}
                  >
                    {saving ? (
                      <>
                        <div className="btn-spinner"></div>
                        {t('conv.saving')}
                      </>
                    ) : (
                      <>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                          <polyline points="17 21 17 13 7 13 7 21"></polyline>
                          <polyline points="7 3 7 8 15 8"></polyline>
                        </svg>
                        {isCreating ? t('conv.createGoal') : t('conv.saveChanges')}
                      </>
                    )}
                  </button>
                  )}
                  {goalType === 'appsflyer' && (
                  <button
                    className="btn-primary"
                    onClick={handleCreateAppsflyer}
                    disabled={saving || !afValid}
                  >
                    {saving ? (
                      <>
                        <div className="btn-spinner"></div>
                        {t('conv.creating')}
                      </>
                    ) : (
                      <>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                          <polyline points="17 21 17 13 7 13 7 21"></polyline>
                          <polyline points="7 3 7 8 15 8"></polyline>
                        </svg>
                        {t('conv.createAppConversion')}
                      </>
                    )}
                  </button>
                  )}
                </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedGoal && (
        <div className="modal-overlay" onClick={() => !isDeleting && setShowDeleteModal(false)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, visibility: 'visible' }}>
          <div className="message-modal" onClick={(e) => e.stopPropagation()}>
            <div className="message-modal-icon delete">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </div>
            <h3>{t('conv.archiveGoalQ')}</h3>
            <p>
              {t('conv.aboutToArchive')} <strong>"{selectedGoal.name}"</strong>.<br/>
              {t('conv.trackingWillStop')}
            </p>
            <div className="message-modal-actions">
              <button
                className="message-modal-btn-secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                {t('conv.cancel')}
              </button>
              <button
                className="message-modal-btn-danger"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <span className="btn-spinner"></span>
                    {t('conv.archiving')}
                  </>
                ) : t('conv.archive')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive App Confirmation Modal */}
      {showArchiveApp && selectedApp && (
        <div className="modal-overlay" onClick={() => !appBusy && setShowArchiveApp(false)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, visibility: 'visible' }}>
          <div className="message-modal" onClick={(e) => e.stopPropagation()}>
            <div className="message-modal-icon delete">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </div>
            <h3>{t('conv.archiveAppQ')}</h3>
            <p>
              {t('conv.aboutToArchive')} <strong>"{selectedApp.name || selectedApp.af_app_id}"</strong>.<br/>
              {t('conv.archiveAppBody')}
            </p>
            <div className="message-modal-actions">
              <button className="message-modal-btn-secondary" onClick={() => setShowArchiveApp(false)} disabled={appBusy}>
                {t('conv.cancel')}
              </button>
              <button className="message-modal-btn-danger" onClick={handleArchiveApp} disabled={appBusy}>
                {appBusy ? (<><span className="btn-spinner"></span>{t('conv.archiving')}</>) : t('conv.archive')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConversionsPage;
